import { legacyPath } from "../lib/prettyUrls";
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

const LEAGUE_KEY = "rosterly_selected_league";

const LeagueContext = createContext({ league: "pwhl", isNcaa: false, setLeague: () => {} });

export const useLeague = () => useContext(LeagueContext);

export function LeagueProvider({ children }) {
    const qc = useQueryClient();
    // Only PWHL and NHL are live leagues. Any stale/removed league (NCAA, SDHL,
    // International) stored from before falls back to PWHL.
    const ALLOWED_LEAGUES = ["pwhl", "nhl", "international", "ncaa"];
    // Per-tab league: each browser tab keeps its OWN selected league so multiple
    // tabs don't override each other. A brand-new tab (middle-click / ctrl-click
    // a nav link) has empty sessionStorage, so we FIRST derive the league from the
    // URL — the /nhl path prefix, or a one-shot ?lg= hint the nav links stamp on
    // new-tab opens — so the new tab inherits the ORIGINATING tab's league instead
    // of a stale global localStorage value. Only then do we fall back to
    // sessionStorage → localStorage → pwhl.
    const [league, setLeagueState] = useState(() => {
        try {
            const path = legacyPath() || "";
            if (path.startsWith("/nhl")) { sessionStorage.setItem(LEAGUE_KEY, "nhl"); sessionStorage.setItem("rosterly_league_applied", "1"); return "nhl"; }
            if (path.startsWith("/nat")) { sessionStorage.setItem(LEAGUE_KEY, "international"); sessionStorage.setItem("rosterly_league_applied", "1"); return "international"; }
            if (path.startsWith("/ncaa")) { sessionStorage.setItem(LEAGUE_KEY, "ncaa"); sessionStorage.setItem("rosterly_league_applied", "1"); return "ncaa"; }
            const qlg = new URLSearchParams(window.location.search).get("lg");
            if (ALLOWED_LEAGUES.includes(qlg)) { sessionStorage.setItem(LEAGUE_KEY, qlg); sessionStorage.setItem("rosterly_league_applied", "1"); return qlg; }
            const s = sessionStorage.getItem(LEAGUE_KEY);
            if (ALLOWED_LEAGUES.includes(s)) return s;
            const l = localStorage.getItem("rosterly_fav_league") || localStorage.getItem(LEAGUE_KEY);
            const fl = localStorage.getItem("rosterly_fav_league");
            const seed = ALLOWED_LEAGUES.includes(l) && (l !== "international" || fl === "international") ? l : "pwhl";
            sessionStorage.setItem(LEAGUE_KEY, seed);
            return seed;
        } catch { return "pwhl"; }
    });
    // Strip the one-shot ?lg hint from the address bar so it never lingers/shares.
    useEffect(() => {
        try {
            const u = new URL(window.location.href);
            if (u.searchParams.has("lg")) { u.searchParams.delete("lg"); window.history.replaceState({}, "", u.pathname + (u.search || "") + u.hash); }
        } catch { /* noop */ }
    }, []);
    const setLeague = useCallback((lg) => {
        setLeagueState(lg);
        try {
            sessionStorage.setItem(LEAGUE_KEY, lg);   // this tab only
            if (lg !== "international" && lg !== "ncaa") localStorage.setItem(LEAGUE_KEY, lg);      // default for future new tabs (International is an extra, never the default)
        } catch { /* noop */ }
        // Every data query is league-scoped (the api client stamps ?league=… on GETs),
        // so refetch everything to swap the whole app to the new league's data.
        qc.invalidateQueries();
    }, [qc]);
    return (
        <LeagueContext.Provider value={{ league, isNcaa: league === "ncaa", isNhl: league === "nhl", isNat: league === "international", setLeague }}>
            {children}
        </LeagueContext.Provider>
    );
}
